import type { Entry } from 'contentful';
import type { IContentPage } from '~~/lib/ContentfulService';
import { isContentPage } from '~/utils/guards/isContentPage';
import { isDefined } from '~/utils/guards/isDefined';
import { buildUrlString } from '~/utils/buildUrlString';
import { usePageTitleState } from '~/composables/usePageTitleState';
import { useSeoAlternateUrls } from '~/composables/useSeoAlternateUrls';
import { useHostname } from '~/composables/useHostname';
import type { PageTeaserMeta } from '~/types/PageTeaserMeta';
import { extractPageTeaserMeta } from '~~/lib/ContentfulTransformerService/helper/extractPageTeaserMeta';

export function calculateCanonical(
  canonical: Entry<unknown> | undefined,
  locale?: string
): string | undefined {
  if (!isContentPage(canonical)) {
    return;
  }
  const slug = canonical?.fields?.slug;

  if (!slug) {
    return;
  }

  return buildUrlString(
    locale ?? canonical.sys.locale,
    slug,
    undefined,
    undefined,
    true
  );
}

export function calculateRobots(page?: IContentPage) {
  return [page?.fields?.seoRobotsIndexTag, page?.fields?.seoMetaRobotsFollowTag]
    .filter(isDefined)
    .join(',');
}

export function calculateTitle(
  page: IContentPage | undefined,
  companyName: string
) {
  return page?.fields?.seoTitle
    ? `${page.fields.seoTitle} | ${companyName}`
    : companyName;
}

export function calculateHead(
  page: IContentPage | undefined,
  companyName: string,
  canonical: string,
  fallbackLocale?: string,
  pageTeaserMeta?: PageTeaserMeta
) {
  const teaserMeta = pageTeaserMeta ?? extractPageTeaserMeta(page);

  return {
    title: calculateTitle(page, companyName),
    htmlAttrs: {
      lang: page?.sys.locale ?? fallbackLocale
    },
    meta: [
      {
        name: 'description',
        content: page?.fields?.seoDescription?.trim() ?? ''
      },
      {
        name: 'robots',
        content: calculateRobots(page)
      },
      {
        property: 'og:type',
        content: 'website'
      },
      {
        property: 'og:url',
        content: canonical
      },
      {
        property: 'og:site_name',
        content: companyName
      },
      {
        property: 'og:title',
        content: teaserMeta?.title ?? ''
      },
      {
        property: 'og:description',
        content: teaserMeta?.description ?? ''
      },
      {
        property: 'og:image',
        content: teaserMeta?.imageUrl ?? undefined
      }
    ]
  };
}

export function useMetadata(
  page: IContentPage | undefined,
  companyName: string,
  pageTeaserMeta?: PageTeaserMeta
) {
  const route = useRoute();
  const serverUrlOrigin = useHostname();
  const locale = useLocale();
  const pageTitle = usePageTitleState();

  const calculatedCanonical = calculateCanonical(page?.fields?.seoCanonicalTag);
  const canonical =
    (calculatedCanonical
      ? calculatedCanonical
      : serverUrlOrigin + route.path) ?? '';
  const head = calculateHead(
    page,
    companyName,
    canonical,
    locale.value,
    pageTeaserMeta
  );

  useHead(head);

  // necessary to be set in analytics
  pageTitle.value = head.title;

  if (page?.fields.seoRobotsIndexTag !== 'NOINDEX') {
    useSeoAlternateUrls();

    useHead({
      link: [
        {
          rel: 'canonical',
          href: canonical
        }
      ]
    });
  }
}
